import { AxiosInstance } from 'axios';
import type { AppDispatch } from '../store/store';
import { addFetch, removeFetch, setToastMessage } from '../slices/utilSlice';
import { errorStatusTypeMap } from '../../library/constants/responseConstants';
import { callLogout } from '../slices/authSlice';
import { isSilentRequest } from './silentRequest';

/**
 * Wires up the request/response interceptors that drive the global loading
 * overlay (CHKM-4023) on the given axios instance. Extracted out of App.tsx so it
 * can be registered from a useEffect (with proper eject() cleanup) and unit
 * tested against a real axios instance without rendering the whole app tree.
 *
 * The request interceptor increments `activeRequests` for every non-silent
 * request and stamps its silence decision onto the config itself (`__silent`,
 * Item 13); the response/error interceptors read that stamped flag back — rather
 * than re-deriving it from the URL — and decrement `activeRequests` only for
 * requests that actually incremented it, so a silenced request's response can
 * never decrement a counter it never incremented and prematurely hide the (now
 * click-blocking) overlay out from under a real in-flight page load. On the
 * error path specifically, an error that carries no `config` at all (axios
 * attaches `config` to essentially every error it produces in practice, so this
 * is a defensive edge case rather than a common one) is treated as "unknown"
 * rather than "not silent" — it cannot be attributed to any particular
 * increment, so it must not decrement one it may never have caused.
 *
 * Returns a cleanup function that ejects both interceptors.
 */
export const registerLoadingInterceptors = (api: AxiosInstance, dispatch: AppDispatch): (() => void) => {
  const requestInterceptorId = api.interceptors.request.use(function (config) {
    // CHKM-4023 round 2 (Item 13): stamp the silence decision onto the config
    // itself at the moment it's made, rather than re-deriving it from the URL
    // on the response/error side — see the error handler below for why the
    // error path additionally guards on `error.config` being present at all.
    (config as { __silent?: boolean }).__silent = isSilentRequest(config);
    if (!(config as { __silent?: boolean }).__silent) {
      dispatch(addFetch());
    }
    return config;
  }, function (error) {
    return Promise.reject(error);
  });

  const responseInterceptorId = api.interceptors.response.use(function (response) {
    // A fulfilled response always carries `.config` (axios sets it as part of
    // dispatchRequest), unlike the error path below, so no defensive
    // null-check is needed here.
    if (!(response.config as { __silent?: boolean }).__silent) {
      dispatch(removeFetch());
    }
    return response;
  }, function (error) {
    // CHKM-4023 round 2 (Item 13): require `error.config` to be present before
    // reading `__silent` off it — an error with no config at all cannot be
    // attributed to a specific request's increment/decrement decision, so it
    // must not decrement a counter it may never have incremented (the round-1
    // asymmetry this item fixes). `isSilentRequest(undefined)` returning
    // `false` made the old re-derivation-based check treat "unknown" the same
    // as "not silent" — always decrementing; requiring the stamped config
    // fixes that instead of merely relocating it.
    if ((error.config as { __silent?: boolean } | undefined) && !(error.config as { __silent?: boolean }).__silent) {
      dispatch(removeFetch());
    }
    if (!error.response) {
      return Promise.reject(error);
    }
    const statusType = `${error.response.status.toString()[0]}xx`;
    const message = `${error.response.status}: ${error.response.data.responseMessage || error.response.data.message}`;
    dispatch(setToastMessage({
      type: errorStatusTypeMap[statusType],
      message
    }));
    if (error.response.status === 401 && (message.includes('expired') || message.includes('token'))) {
      dispatch(callLogout());
    }
    return Promise.reject(error);
  });

  return () => {
    api.interceptors.request.eject(requestInterceptorId);
    api.interceptors.response.eject(responseInterceptorId);
  };
};
